// Liquid terrain (docs/28): what each liquid square is made of and how its surface moves under the
// fight's sky. Pure, so the encounter's `LiquidLayer`, the gallery and the overworld chart's
// `MapCanvas` all read one table and tests can check the caps.
//
// A liquid is decided by how the kit *draws* the square, so the picture and the effect always
// agree: a `Water`/`Deep` square takes the kit's water set (water, or lava in the ember cave and the
// Ashlands — the sim's `Land::Ember` and `Land::Ashlands` read `Water` as `Class::Lava`), and a `Bad` square in a kit whose rough is a
// liquid set (the mirewood's and jungle's bog mud) takes that set.
//
// Every effect is small and slow — a sparkle, a lapping line, a bubble, a ring — and most of it only
// lightens; the one darkening (the deep's swell) is capped well inside the docs/19 floors.
import type { TileData } from '../../core/sim/types';
import type { DayLight, RGB } from '../lighting/daylight';
import { mix } from '../lighting/daylight';
import { LIQUID, type LiquidSet } from '../tiling/oryx';
import type { Kit } from '../tiling/types';
import type { Season } from '../weather/season';
import type { Weather } from '../weather/weather';

export type Liquid = 'water' | 'deep' | 'lava' | 'mud';
export const LIQUIDS: readonly Liquid[] = ['water', 'deep', 'lava', 'mud'];

/** The mask code a liquid is written as (R channel = code × 50). */
export const LIQUID_CODE: Readonly<Record<Liquid, number>> = { water: 1, deep: 2, lava: 3, mud: 4 };

/** The most a sparkle, a lap of foam or a ring may lighten a pixel toward white. */
export const GLINT_CAP = 0.42;
/** The most the deep's swell may darken it. */
export const SWELL_CAP = 0.1;
/** The most the lava's moving veins may brighten it. */
export const LAVA_CAP = 0.5;

function material(set: LiquidSet, deep: boolean): Liquid | undefined {
  if (set === LIQUID.lava) return 'lava';
  if (set === LIQUID.mud) return 'mud';
  if (set === LIQUID.water) return deep ? 'deep' : 'water';
  return undefined;
}

/** What a square is made of, as `kit` draws it (undefined for dry ground). */
export function liquidOf(t: Pick<TileData, 'terrain'> | undefined, kit: Pick<Kit, 'water' | 'rough'>): Liquid | undefined {
  if (!t) return undefined;
  if (t.terrain === 'Water' || t.terrain === 'Deep') return material(kit.water.set, t.terrain === 'Deep');
  if (t.terrain === 'Bad' && kit.rough.kind === 'liquid') return material(kit.rough.set, false);
  return undefined;
}

/** Every square's liquid, row by row. */
export function liquidCells(board: { w: number; h: number; tiles: ReadonlyArray<Pick<TileData, 'terrain'> | undefined> }, kit: Pick<Kit, 'water' | 'rough'>): Array<Liquid | undefined> {
  const out: Array<Liquid | undefined> = [];
  for (let i = 0; i < board.w * board.h; i++) out.push(liquidOf(board.tiles[i], kit));
  return out;
}

/** The kits whose land is under the sky (`fk_sim::field::Land::outdoors`). */
const OUTDOOR_KITS = new Set(['mirewood', 'jungle', 'forest', 'autumn', 'pinewood', 'winterwood', 'dunes', 'highland', 'underdark', 'ashlands']);

/**
 * Whether the water and bog are frozen, exactly as the rules read it (`Environment::frozen` in
 * field.rs): outdoors, never in the jungle, in a frost or in a snowfield's winter. Frozen water is
 * `Class::Ice` — walkable — so it must look like ice, not like water. Lava never freezes.
 */
export function liquidFrozen(kit: string, weather: Pick<Weather, 'kind'>, season: Season | undefined): boolean {
  if (!OUTDOOR_KITS.has(kit) || kit === 'jungle') return false;
  return weather.kind === 'frost' || (season === 'winter' && kit === 'winterwood');
}

/** One liquid surface under one sky: everything the shader and the particles draw. */
export interface LiquidLook {
  /** Water: sparkles where the wavelets cross (0..GLINT_CAP), the lapping foam line along the bank, the deep's swell (0..SWELL_CAP). */
  glint: number; lap: number; swell: number;
  /** Wind on open water: whitecaps on the deep and a faster chop, 0..1. */
  chop: number;
  /** Which way the surface drifts (art px per second, the wind's way; a still day drifts gently east). */
  flow: [number, number];
  /** Rain rings: how many of the ring slots are live (0..1) and how big a ring grows (art px). */
  rings: number; ringSize: number;
  /** Snowflakes and sleet melting on the water: tiny dimples, 0..1. */
  dimples: number;
  /** A cave's drips landing in its pools: big slow rings, 0..1. */
  drips: number;
  /** Lava: the veins' brightness (0..LAVA_CAP) and how often it bubbles. */
  lava: number; lavaBubbles: number;
  /** Bog mud: the sheen and how often it bubbles. */
  sheen: number; mudBubbles: number;
  /** Frozen (`liquidFrozen`): water and mud drawn as ice with cracks and glints, 0..1. */
  ice: number;
  /** Rain or snow hissing into lava. */
  steam: number;
  /** The odd fish leaping in open water by day. */
  fish: boolean;
  /** Things drifting on the water: autumn leaves, spring petals. */
  floaters: 'leaves' | 'petals' | undefined;
  /** The colour the water reflects and its sparkles take. */
  sky: RGB;
  /** The light on the surface (luma of the ambient, 0..1): sparkles fade at night but never vanish. */
  light: number;
}

const luma3 = (c: RGB): number => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];

/** The surface under `weather` in `day` (the day already weathered and seasoned by the lighting). */
export function liquidLook(day: DayLight, weather: Weather, opts: { frozen: boolean; season?: Season }): LiquidLook {
  const indoor = day.setting === 'indoor';
  const k = weather.intensity;
  const kind = weather.kind;
  const light = Math.min(1, luma3(day.ambient));
  const sunlit = !!day.sun && !day.sun.moon;
  const wind = kind === 'clear' && k === 0 ? 0.25 : weather.wind;
  const gust = Math.abs(wind);
  const outside = !indoor;
  const rain = !outside ? 0 : kind === 'rain' ? 0.35 + 0.55 * k : kind === 'storm' ? 0.8 + 0.2 * k : kind === 'sleet' ? 0.25 + 0.35 * k : 0;
  const snow = !outside ? 0 : kind === 'snow' ? 0.3 + 0.5 * k : kind === 'blizzard' ? 0.7 + 0.3 * k : kind === 'sleet' ? 0.3 : 0;
  // a storm or a gale whips the open water; a still or foggy day leaves it glassy
  const chop = outside ? Math.min(1, kind === 'storm' || kind === 'blizzard' ? 0.55 + 0.45 * gust : kind === 'sandstorm' ? 0.4 * gust : Math.max(0, gust - 0.45) * 0.8) : 0;
  const glassy = kind === 'fog' || kind === 'overcast' ? 0.7 : 1;
  const ice = opts.frozen ? 1 : 0;
  return {
    glint: Math.min(GLINT_CAP, GLINT_CAP * (sunlit ? 0.95 : 0.5 + 0.4 * light) * glassy * (1 - 0.35 * Math.min(1, rain + snow))),
    lap: Math.min(GLINT_CAP, 0.32 * (0.6 + 0.4 * light) + 0.08 * chop),
    swell: Math.min(SWELL_CAP, 0.06 + 0.04 * chop),
    chop,
    flow: [(wind < 0 ? -1 : 1) * (1.2 + 5 * gust), 0.6 + 1.2 * gust],
    rings: Math.min(1, rain * 1.1),
    ringSize: kind === 'sleet' ? 2.6 : kind === 'storm' ? 4.4 : 3.8,
    dimples: Math.min(1, snow),
    drips: indoor && day.atmosphere.drips ? 1 : 0,
    lava: LAVA_CAP * (0.75 + 0.25 * (1 - light)),
    lavaBubbles: 1,
    sheen: 0.09 * (0.5 + 0.5 * light) * glassy,
    mudBubbles: kind === 'heat' || opts.season === 'summer' ? 1.3 : 1,
    ice,
    steam: Math.min(1, rain + snow * 0.8),
    fish: outside && !ice && sunlit && rain === 0 && snow === 0 && kind !== 'storm' && kind !== 'sandstorm',
    floaters: ice || !outside ? undefined : opts.season === 'autumn' || day.atmosphere.leaves ? 'leaves' : day.atmosphere.petals ? 'petals' : undefined,
    sky: mix(day.sky, [1, 1, 1], 0.25),
    light,
  };
}
